import { useState, useEffect } from "react";
import { __ } from "@wordpress/i18n";
import apiFetch from "@wordpress/api-fetch";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import ContentHeader from "../../components/ContentHeader";

const BackupRestoreRole = () => {
	const [isSaving, setIsSaving] = useState(false);
	const [isSaved, setIsSaved] = useState(false);
	const [isRestoring, setIsRestoring] = useState(false);
	const [isRestored, setIsRestored] = useState(false);
	const [lastBackupTime, setLastBackupTime] = useState('');
  
	// Fetch last backup time when component mounts
	useEffect(() => {
	  fetchLastBackupTime();
	}, []);
  
	const fetchLastBackupTime = async () => {
	  try {
		const response = await apiFetch({
		  path: '/plac/v1/backup/last-time',
		  method: 'GET',
		});
		
		if (response.success) {
		  setLastBackupTime(response.data.lastBackupTime);
		}
	  } catch (error) {
		console.error('Error fetching last backup time:', error);
	  }
	};
  
	const createBackup = async (e) => {
		e.preventDefault();
		setIsSaving(true);
		setIsSaved(false); 
		try {
			const response = await apiFetch({
				path: '/plac/v1/create-backup',
				method: 'POST',
				data: {
					_wpnonce: window.placAdminData.nonce,
				},
			});
	
			if (response.success) {
				setLastBackupTime(response.data.lastBackupTime);
			} else {
				throw new Error('Failed to create backup');
			}
	
			setTimeout(() => {
				setIsSaving(false);
				setIsSaved(true);
				setTimeout(() => setIsSaved(false), 1000);
			}, 500);
		} catch (error) {
			console.error('Error creating backup:', error);
			setIsSaving(false);
		} 
	};
	
	const restoreBackup = async (e) => {
		e.preventDefault();
		setIsRestoring(true);
		setIsRestored(false);
		try {
			const response = await apiFetch({
				path: '/plac/v1/restore-backup',
				method: 'POST',
				data: {
					_wpnonce: window.placAdminData.nonce,
				},
			});
	
			if (response.success) {
				console.log('Backup restored successfully');
			} else {
				throw new Error(response.message || 'Failed to restore backup');
			}
	
			setTimeout(() => {
				setIsRestoring(false);
				setIsRestored(true);
				setTimeout(() => setIsRestored(false), 1000);
			}, 500);
		} catch (error) {
			console.error('Error restoring backup:', error);
			setIsRestoring(false);
		}
	};
	
  
	return (
	  <>
	
		<div className="settings-container">
		  <ContentHeader 
			title={__("Backup and restore your roles", "access-control")} 
			description={__("Use this section to backup your current user roles' and their configuration. You can also restore this data at any time by clicking the Restore backup button.", "access-control")} 
		  />
		  
		  
		  <div className="content-section">
			<div className="leftSection">
			  <h2 className="title header-1 primary-text-color semi-bold">
				{__("Create Backup", "access-control")}
			  </h2>
			</div>
			<div className="rightSection">
			  <div className="setting-item">
				<div className="input-container">
				  <OverlayTrigger 
					placement="top" 
					overlay={
					  <Tooltip id="create-backup-tooltip">
						{__("Create Backup of user role and capabilities", "access-control")}
					  </Tooltip>
					}
				  >
					<img 
					  src={`${window.placAdminData.pluginUrl}/assets/images/question.svg`} 
					  alt="icon" 
					  className="icon" 
					  style={{ cursor: "pointer" }} 
					/>
				  </OverlayTrigger>
				  <p className="header-2 primary-text-color regular" style={{margin:0}}>
					{__("Here you can manually create a backup of your current configuration. This backup will be automatically updated if you make changes using Access Control", "access-control")}
				  </p>
				</div>
				<div className="global-reset">
					{isSaving ? (
						<div
							className="saving-changes"
							style={{
								paddingLeft: "20px",
								color: "#873eff",
							}}
						>
							<div className="pl-access-control-loading-spinner"></div>
							{__("Backup Creating...", "access-control")}
						</div>
					) : isSaved ? (
						<div
							className="success-checkmark"
							style={{
								paddingLeft: "20px",
								color: "#873eff",
							}}
						>
							<img
								src={`${window.placAdminData.pluginUrl}/assets/images/success-tick-active.svg`}
								alt="quotation-icon"
							/>
							{__("Changes Saved", "access-control")}
						</div>
					) : (
						<a
							href="#"
							onClick={createBackup}
							className="reset-txt-btn header-1 semi-bold purple-text-color"
						>
							{__(
								"Create Backup",
								"access-control",
							)}
						</a>
					)}
				</div>
				{lastBackupTime && (
				  <p className="mt-2" style={{marginLeft:"20px"}}>
					<span className="semi-bold">{__("Last backup time:", "access-control")}</span> {lastBackupTime}
				  </p>
				)}
			  </div>
			</div>
		  </div>
		  
		  <div className="content-section">
			<div className="leftSection">
			  <h2 className="title header-1 primary-text-color semi-bold">
				{__("Restore Backup", "access-control")}
			  </h2>
			</div>
			<div className="rightSection">
			  <div className="setting-item">
				<div className="input-container">
				  <OverlayTrigger 
					placement="top" 
					overlay={
					  <Tooltip id="current-role-tooltip">
						{__("Restore Backup", "access-control")}
					  </Tooltip>
					}
				  >
					<img 
					  src={`${window.placAdminData.pluginUrl}/assets/images/question.svg`} 
					  alt="icon" 
					  className="icon" 
					  style={{ cursor: "pointer" }} 
					/>
				  </OverlayTrigger>
				  <p className="header-2 primary-text-color regular" style={{margin:0}}>
					{__("Here you can restore your current WP roles from the most recent backup.", "access-control")}
				  </p>
				</div>
				<div className="global-reset">
					{isRestoring ? (
						<div
							className="saving-changes"
							style={{
								paddingLeft: "20px",
								color: "#873eff",
							}}
						>
							<div className="pl-access-control-loading-spinner"></div>
							{__("Restoring...", "access-control")}
						</div>
					) : isRestored ? (
						<div
							className="success-checkmark"
							style={{
								paddingLeft: "20px",
								color: "#873eff",
							}}
						>
							<img
								src={`${window.placAdminData.pluginUrl}/assets/images/success-tick-active.svg`}
								alt="quotation-icon"
							/>
							{__("Changes Saved", "access-control")}
						</div>
					) : (
						<a
							href="#"
							onClick={restoreBackup}
							className="reset-txt-btn header-1 semi-bold purple-text-color"
						>
							{__(
								"Retore Backup",
								"access-control",
							)}
						</a>
					)}
				</div>
			  </div>
			</div>
		  </div>
		  
		  <div className="footer-section">
			<button className="save-button disabled purple-bg-40 secondary-text-color" disabled>
			  {__("Save Change", "access-control")}
			</button>
			<button className="discard-button disabled purple-text-color btn-border" disabled>
			  {__("Reset to Default", "access-control")}
			</button>
		  </div>
		</div>
	  </>
	);
  };

export default BackupRestoreRole;
